/*
 * Typed API client.
 *
 * Same-origin: nginx serves the dashboard and proxies /v1 to the API, so the session
 * cookie travels automatically. No CORS surface, and no token in localStorage for a
 * script injection to steal.
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    /** Stable machine-readable code, e.g. domain_not_verified. Safe to branch on. */
    readonly code: string | undefined,
    /** Quote this in a support ticket and the exact request can be looked up. */
    readonly requestId: string | undefined,
    message: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`/v1${path}`, {
    ...init,
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
  })

  const requestId = res.headers.get('X-Request-Id') ?? undefined
  if (res.status === 204) return undefined as T

  const text = await res.text()
  const body = text ? safeJson(text) : undefined

  if (!res.ok) {
    throw new ApiError(
      res.status,
      body?.code,
      body?.request_id ?? requestId,
      // Prefer the server's own wording: it is written for the customer and explains
      // the fix, which a generic "Request failed" never does.
      body?.detail ?? body?.title ?? `Request failed (${res.status})`,
    )
  }

  return body as T
}

function safeJson(text: string): any {
  try {
    return JSON.parse(text)
  } catch {
    return { detail: text.slice(0, 300) }
  }
}

// Exported for the campaign module's client (lib/cm.ts).
export const get = <T,>(p: string) => request<T>(p)
export const post = <T,>(p: string, body?: unknown) =>
  request<T>(p, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) })
export const patch = <T,>(p: string, body?: unknown) =>
  request<T>(p, { method: 'PATCH', body: body === undefined ? undefined : JSON.stringify(body) })
export const del = <T,>(p: string) => request<T>(p, { method: 'DELETE' })

// ---------------------------------------------------------------- types

export type TenantStatus = 'pending_review' | 'sandbox' | 'active' | 'suspended' | 'closed'

export type DomainStatus =
  | 'pending' | 'partial' | 'verified_sending' | 'verified_inbound' | 'drifted' | 'suspended'

export type RecordStatus = 'pending' | 'ok' | 'mismatch' | 'missing'

export interface DnsRecord {
  type: 'TXT' | 'CNAME' | 'MX'
  host: string
  value: string
  ttl: number
  purpose: string
  required: boolean
  status: RecordStatus
  observed?: string | null
  hint?: string | null
  last_checked_at?: string | null
}

export interface Domain {
  id: string
  name: string
  status: DomainStatus
  can_send: boolean
  can_receive: boolean
  inbound_enabled: boolean
  return_path_host: string
  tracking_host?: string | null
  verified_at?: string | null
  last_checked_at?: string | null
  created_at: string
  dns_records?: DnsRecord[]
}

export interface BillingPlan {
  name: string; is_free: boolean; daily_send_limit: number; hourly_send_limit: number
  max_domains: number; max_recipients_per_message: number; dedicated_ip_allowed: boolean
  tracking_allowed: boolean; inbound_retention_days: number
}
export interface BillingInfo {
  current_plan: string | null
  free_allowance_remaining: number
  usage_this_month: { accepted: number; delivered: number }
  plans: BillingPlan[]
}

export interface BounceReport {
  summary: { window_days: number; bounced: number; bounced_hard: number; bounced_soft: number; complained: number; delivered: number }
  data: Array<{
    address: string; status: string; message_id: string; subject: string | null
    smtp_code: string | null; dsn: string | null; response: string | null; remote_mx: string | null
    attempts: number; at: string
  }>
}

export type Verdict = 'good' | 'warning' | 'critical'
export interface HealthReport {
  overall: Verdict
  window_days: number
  deliverability: {
    verdict: Verdict
    delivered: number; bounced: number; complained: number; attempted: number
    bounce_rate: number; complaint_rate: number
    bounce_warn: number; bounce_crit: number; complaint_warn: number; complaint_crit: number
  }
  domains: Array<{
    name: string; status: string; inbound_enabled: boolean
    spf: string; dkim: string; dmarc: string; return_path: string
    verdict: Verdict; issues: string[]
  }>
  sending_ips: Array<{ address: string; status: string; reputation: number; blocklisted: boolean; listings: string | null }>
  volume: Array<{ date: string; delivered: number; bounced: number; deferred: number }>
}

export interface RouteCondition { field: string; op: string; value: string | null }
export interface RouteAction { type: string; to?: string | null; url?: string | null }
export interface RouteInput {
  condition: RouteCondition
  actions: RouteAction[]
  priority?: number
  description?: string
}
export interface RouteSummary {
  id: string
  priority: number
  condition: RouteCondition | null
  actions: RouteAction[]
  description: string | null
  active: boolean
  match_count: number
  last_matched_at: string | null
  created_at: string
}

export interface StreamSummary {
  id: string
  name: string
  slug: string
  type: 'transactional' | 'broadcast'
  description: string | null
  is_default: boolean
  active: boolean
  created_at: string
  updated_at: string
}

export interface TemplateSummary {
  id: string
  name: string
  slug: string
  description: string | null
  subject: string
  has_html: boolean
  has_text: boolean
  version: number
  active: boolean
  placeholders: string[]
  created_at: string
  updated_at: string
}

export interface TemplateDetail extends TemplateSummary {
  html: string | null
  text: string | null
}

export interface AccountProfile {
  id: string
  name: string | null
  email: string
  role: string
  email_verified: boolean
  pending_email: string | null
  mfa_enabled: boolean
  created_at: string
  last_login_at?: string | null
}

export interface Me {
  tenant: {
    id: string
    name: string
    status: TenantStatus
    can_send: boolean
    is_sandboxed: boolean
    created_at: string
    approved_at?: string | null
    suspension_reason?: string | null
  }
  plan?: {
    name: string
    daily_send_limit: number
    hourly_send_limit: number
    max_domains: number
    dedicated_ip_allowed: boolean
  } | null
  user: { email?: string; role?: string }
  counts: { domains: number; verified_domains: number; api_keys: number }
  kyc?: {
    status: KycStatus
    account_type: 'individual' | 'company'
    review_note?: string | null
    daily_limit: number
    transactional_percent: number
  } | null
}

export type KycStatus = 'not_submitted' | 'submitted' | 'more_info' | 'approved' | 'rejected'

export interface SendingLimits {
  daily_limit: number
  transactional_percent: number
  transactional_daily: number
  marketing_daily: number
  /** Only on GET /account/limits: marketing emails (campaigns + bulk) still available today. */
  marketing_remaining_today?: number
}

export interface KycDocumentInfo {
  id: string; kind: string; label: string; file_name: string
  content_type: string; size_bytes: number; uploaded_at: string
}

export interface KycInfo {
  status: KycStatus
  account_type: 'individual' | 'company'
  contact_name?: string | null
  phone?: string | null
  country?: string | null
  website?: string | null
  submitted_at?: string | null
  reviewed_at?: string | null
  review_note?: string | null
  tenant_status: string
  required: { kind: string; label: string; uploaded: boolean }[]
  kinds: { kind: string; label: string }[]
  documents: KycDocumentInfo[]
  limits: SendingLimits
}

export interface AdminKycRow {
  tenant_id: string; name: string; owner_email?: string | null; tenant_status: string
  account_type: string; country?: string | null; kyc_status: KycStatus; documents: number
  submitted_at?: string | null; created_at: string; daily_limit: number
}

export interface AdminKycDetail {
  verification: KycInfo
  tenant: {
    id: string; name: string; legal_name?: string | null; status: string; plan?: string | null
    owner_email?: string | null; signup_ip?: string | null; use_case?: string | null
    expected_volume?: number | null; created_at: string; approved_at?: string | null
  }
}

/** Multipart upload: lets the browser set the multipart boundary (no JSON content type). */
async function upload<T>(path: string, form: FormData): Promise<T> {
  const res = await fetch(`/v1${path}`, { method: 'POST', body: form, credentials: 'same-origin' })
  const text = await res.text()
  const body = text ? safeJson(text) : undefined
  if (!res.ok) {
    throw new ApiError(res.status, body?.code, body?.request_id, body?.detail ?? body?.title ?? `Upload failed (${res.status})`)
  }
  return body as T
}

const put = <T,>(p: string, body?: unknown) =>
  request<T>(p, { method: 'PUT', body: body === undefined ? undefined : JSON.stringify(body) })

export const kycApi = {
  get: () => get<KycInfo>('/kyc'),
  upload: (kind: string, file: File) => {
    const f = new FormData()
    f.append('kind', kind)
    f.append('file', file)
    return upload<KycDocumentInfo>('/kyc/documents', f)
  },
  remove: (id: string) => del<void>(`/kyc/documents/${id}`),
  submit: () => post<KycInfo>('/kyc/submit'),
  limits: () => get<SendingLimits>('/account/limits'),
  setSplit: (transactional_percent: number) => put<SendingLimits>('/account/limits', { transactional_percent }),
  admin: {
    list: (status?: string) => get<{ data: AdminKycRow[] }>(`/admin/kyc${status ? `?status=${encodeURIComponent(status)}` : ''}`),
    detail: (tenantId: string) => get<AdminKycDetail>(`/admin/kyc/${tenantId}`),
    documentUrl: (docId: string) => get<{ url: string }>(`/admin/kyc/documents/${docId}/url`),
    approve: (tenantId: string, b: { reason?: string; daily_limit?: number; transactional_percent?: number }) =>
      post<KycInfo>(`/admin/kyc/${tenantId}/approve`, b),
    reject: (tenantId: string, reason: string) => post<KycInfo>(`/admin/kyc/${tenantId}/reject`, { reason }),
    requestInfo: (tenantId: string, reason: string) => post<KycInfo>(`/admin/kyc/${tenantId}/request-info`, { reason }),
    setLimits: (tenantId: string, b: { daily_limit?: number; transactional_percent?: number }) =>
      put<SendingLimits>(`/admin/kyc/${tenantId}/limits`, b),
  },
}

export interface VerifyResult {
  domain: string
  status: DomainStatus
  can_send: boolean
  can_receive: boolean
  records: DnsRecord[]
  /** Only the records actually blocking verification, so the UI can lead with the fix. */
  blocking: Array<{
    type: string
    host: string
    expected: string
    observed?: string | null
    hint?: string | null
  }>
}

export interface ApiKey {
  id: string
  name: string
  prefix: string
  scopes: string
  test: boolean
  last_used_at?: string | null
  last_used_ip?: string | null
  expires_at?: string | null
  revoked_at?: string | null
  created_at: string
}

export interface CreatedApiKey extends ApiKey {
  /** Returned exactly once. We store only a hash, so it can never be shown again. */
  secret: string
  warning: string
}

export interface Message {
  id: string
  status: string
  from: string
  to?: string | null
  subject?: string | null
  recipients: number
  created_at: string
  has_attachments?: boolean
  opened?: boolean
  clicked?: boolean
  delivered?: boolean
  bounced?: boolean
  reason?: string | null
  channel?: string
}

export interface SmtpCredential {
  id: string
  name: string
  username: string
  allowed_domains?: string | null
  max_messages_per_hour: number
  enabled: boolean
  last_used_at?: string | null
  last_used_ip?: string | null
  recent_distinct_countries: number
  disabled_at?: string | null
  disabled_reason?: string | null
  created_at: string
}

export interface CreatedSmtpCredential {
  id: string
  name: string
  username: string
  password: string
  host: string
  ports: number[]
  max_messages_per_hour: number
  warning: string
}

export interface SmtpPort {
  port: number
  tls: string
  note: string
}

export type SuppressionReason =
  | 'hard_bounce' | 'complaint' | 'unsubscribe' | 'manual' | 'soft_bounce_repeated'

export interface Suppression {
  address: string
  reason: SuppressionReason
  smtp_response?: string | null
  expires_at?: string | null
  created_at: string
  removable: boolean
}

export interface Webhook {
  id: string
  url: string
  description: string
  events: string
  enabled: boolean
  consecutive_failures: number
  last_success_at?: string | null
  last_failure_at?: string | null
  last_failure_reason?: string | null
  auto_disabled_at?: string | null
  created_at: string
}

export interface CreatedWebhook {
  id: string
  url: string
  events: string[]
  signing_secret: string
  signature_header: string
  signature_scheme: string
  warning: string
}

export interface ActivityRow {
  occurred_at: string
  action: string
  actor_type: string
  actor_ip?: string | null
  entity_type?: string | null
  before?: string | null
  after?: string | null
  reason?: string | null
}

export interface SignIn {
  occurred_at: string
  result: string
  source_ip: string
  source_country?: string | null
  identity?: string | null
}

export interface DailyStat {
  date: string
  accepted: number
  delivered: number
  bounced: number
  deferred: number
  complained: number
  opened: number
  clicked: number
  suppressed: number
}

export interface Stats {
  range_days: number
  daily: DailyStat[]
  totals: {
    accepted: number; delivered: number; bounced: number; deferred: number
    complained: number; opened: number; clicked: number; suppressed: number
  }
  rates: { delivery: number | null; bounce: number | null; complaint: number | null }
  thresholds: {
    complaint_warning: number; complaint_critical: number
    bounce_warning: number; bounce_critical: number
  }
  recent_messages: {
    id: string; subject?: string | null; from_address: string
    recipient_count: number; status: string; created_at: string
  }[]
}

export type Role = 'SuperAdmin' | 'Owner' | 'Admin' | 'Developer' | 'Viewer'

export interface TeamMember {
  id: string
  email: string
  role: Role
  mfa_enabled: boolean
  last_login_at?: string | null
  last_login_ip?: string | null
  disabled_at?: string | null
  created_at: string
}

// ---------------------------------------------------------------- endpoints

export const api = {
  auth: {
    /**
     * The API serialises AND deserialises in snake_case, so request bodies must use
     * snake_case too. Mapping here keeps the camelCase convention inside the app.
     */
    signup: (b: {
      email: string; password: string; companyName: string
      website?: string; useCase?: string; expectedVolume?: number
      country?: string; acceptTerms: boolean
      accountType?: 'individual' | 'company'; phone?: string; contactName?: string
    }) => post<{ requires_verification?: boolean; tenant: any; user: any; message?: string }>('/auth/signup', {
      email: b.email,
      password: b.password,
      company_name: b.companyName,
      website: b.website,
      use_case: b.useCase,
      expected_volume: b.expectedVolume,
      country: b.country,
      accept_terms: b.acceptTerms,
      account_type: b.accountType,
      phone: b.phone,
      contact_name: b.contactName,
    }),

    login: (b: { email: string; password: string; code?: string; remember?: boolean }) =>
      post<{ tenant: any; user: any }>('/auth/login', b),

    logout: () => post<void>('/auth/logout'),
    me: () => get<Me>('/auth/me'),

    verifyEmail: (b: { email: string; code: string }) =>
      post<{ verified: boolean; status?: string; awaiting_approval?: boolean }>('/auth/verify-email', b),
    resendVerification: (b: { email: string }) =>
      post<{ sent: boolean }>('/auth/resend-verification', b),
    forgotPassword: (b: { email: string }) =>
      post<{ sent: boolean }>('/auth/forgot-password', b),
    resetPassword: (b: { token: string; newPassword: string }) =>
      post<{ reset: boolean }>('/auth/reset-password', { token: b.token, new_password: b.newPassword }),
  },

  templates: {
    list: (includeDisabled = false) =>
      get<{ data: TemplateSummary[] }>(`/templates?include_disabled=${includeDisabled}`),
    get: (id: string) => get<TemplateDetail>(`/templates/${id}`),
    create: (b: { name: string; subject: string; slug?: string; html?: string; text?: string; description?: string }) =>
      post<TemplateSummary>('/templates', b),
    update: (id: string, b: { name?: string; subject?: string; html?: string; text?: string; description?: string }) =>
      patch<TemplateSummary>(`/templates/${id}`, b),
    disable: (id: string) => post<{ id: string; active: boolean }>(`/templates/${id}/disable`),
    enable: (id: string) => post<{ id: string; active: boolean }>(`/templates/${id}/enable`),
    preview: (id: string, variables: Record<string, string>) =>
      post<{ subject: string; html: string | null; text: string | null }>(`/templates/${id}/preview`, { variables }),
    remove: (id: string) => del<void>(`/templates/${id}`),
    seedSamples: () => post<{ created: number; skipped: number; data: TemplateSummary[] }>('/templates/samples'),
  },

  routes: {
    list: () => get<{ data: RouteSummary[] }>('/routes'),
    create: (b: RouteInput) => post<RouteSummary>('/routes', b),
    update: (id: string, b: Partial<RouteInput>) => patch<RouteSummary>(`/routes/${id}`, b),
    disable: (id: string) => post<{ id: string; active: boolean }>(`/routes/${id}/disable`),
    enable: (id: string) => post<{ id: string; active: boolean }>(`/routes/${id}/enable`),
    remove: (id: string) => del<void>(`/routes/${id}`),
  },

  streams: {
    list: () => get<{ data: StreamSummary[] }>('/streams'),
    create: (b: { name: string; type: string; slug?: string; description?: string }) =>
      post<StreamSummary>('/streams', b),
    update: (id: string, b: { name?: string; description?: string }) =>
      patch<StreamSummary>(`/streams/${id}`, b),
    disable: (id: string) => post<{ id: string; active: boolean }>(`/streams/${id}/disable`),
    enable: (id: string) => post<{ id: string; active: boolean }>(`/streams/${id}/enable`),
    remove: (id: string) => del<void>(`/streams/${id}`),
  },

  account: {
    get: () => get<AccountProfile>('/account'),
    updateProfile: (b: { name: string | null }) =>
      patch<{ id: string; name: string | null }>('/account/profile', { name: b.name }),
    changePassword: (b: { currentPassword: string; newPassword: string }) =>
      post<{ changed: boolean }>('/account/change-password',
        { current_password: b.currentPassword, new_password: b.newPassword }),
    changeEmail: (b: { newEmail: string; password: string }) =>
      post<{ pending_email: string }>('/account/change-email',
        { new_email: b.newEmail, password: b.password }),
    verifyEmailChange: (b: { code: string }) =>
      post<{ email: string }>('/account/verify-email-change', b),
    twoFactorSetup: () => post<{ secret: string; otpauth_uri: string }>('/account/2fa/setup'),
    twoFactorEnable: (b: { code: string }) => post<{ enabled: boolean }>('/account/2fa/enable', b),
    twoFactorDisable: (b: { password: string }) => post<{ enabled: boolean }>('/account/2fa/disable', b),
    securityLog: () => get<{ data: Array<{ at: string; type: string; result: string; ip: string; identity: string; detail: string | null }> }>('/account/security-log'),
  },

  domains: {
    list: () => get<{ data: Domain[] }>('/domains'),
    get: (id: string) => get<Domain>(`/domains/${id}`),
    create: (b: { name: string; inbound: boolean }) => post<Domain>('/domains', b),
    dns: (id: string) =>
      get<{ domain: string; status: DomainStatus; required: DnsRecord[]; recommended: DnsRecord[] }>(
        `/domains/${id}/dns`),
    verify: (id: string) => post<VerifyResult>(`/domains/${id}/verify`),
    rotateDkim: (id: string) =>
      post<{ domain: string; message: string; records: DnsRecord[] }>(`/domains/${id}/dkim/rotate`),
    update: (id: string, b: { inbound?: boolean }) => patch<Domain>(`/domains/${id}`, b),
    remove: (id: string) => del<void>(`/domains/${id}`),
  },

  keys: {
    list: () => get<{ data: ApiKey[] }>('/api-keys'),
    create: (b: { name: string; scopes?: string[]; test?: boolean; allowedDomains?: string[]; ipAllowlist?: string[] }) =>
      post<CreatedApiKey>('/api-keys', {
        name: b.name, scopes: b.scopes, test: b.test, allowed_domains: b.allowedDomains,
        ip_allowlist: b.ipAllowlist,
      }),
    revoke: (id: string) => del<void>(`/api-keys/${id}`),
  },

  messages: {
    list: (q: {
      limit?: number; offset?: number; status?: string; search?: string
      from?: string; to?: string; date_from?: string; date_to?: string; sort?: string
      channel?: string; exclude_to?: string; delivery?: string
    } = {}) => {
      const p = new URLSearchParams()
      if (q.limit) p.set('limit', String(q.limit))
      if (q.offset) p.set('offset', String(q.offset))
      if (q.status) p.set('status', q.status)
      if (q.search) p.set('search', q.search)
      if (q.from) p.set('from', q.from)
      if (q.to) p.set('to', q.to)
      if (q.date_from) p.set('date_from', q.date_from)
      if (q.date_to) p.set('date_to', q.date_to)
      if (q.sort) p.set('sort', q.sort)
      if (q.channel) p.set('channel', q.channel)
      if (q.exclude_to) p.set('exclude_to', q.exclude_to)
      if (q.delivery) p.set('delivery', q.delivery)
      return get<{
        data: Message[]; total?: number
        status_counts?: { all: number; accepted: number; queued: number; sent: number; failed: number; blocked: number }
        source_counts?: { api: number; smtp: number; dashboard: number; system: number }
      }>(`/messages?${p}`)
    },
    get: (id: string) => get<any>(`/messages/${id}`),
    send: (b: unknown) => post<{ id: string; recipients: number }>('/messages', b),
    retry: (id: string) => post<{ id: string; retried: number }>(`/messages/${id}/retry`),
  },

  smtp: {
    list: () => get<{ data: SmtpCredential[]; smtp_host: string; ports: SmtpPort[] }>(
      '/smtp-credentials'),
    create: (b: { name: string; allowedDomains?: string[]; maxMessagesPerHour?: number }) =>
      post<CreatedSmtpCredential>('/smtp-credentials', {
        name: b.name,
        allowed_domains: b.allowedDomains,
        max_messages_per_hour: b.maxMessagesPerHour,
      }),
    remove: (id: string) => del<void>(`/smtp-credentials/${id}`),
  },

  suppressions: {
    list: (q: { reason?: string; search?: string; limit?: number; offset?: number } = {}) => {
      const p = new URLSearchParams()
      if (q.reason) p.set('reason', q.reason)
      if (q.search) p.set('search', q.search)
      if (q.limit) p.set('limit', String(q.limit))
      if (q.offset) p.set('offset', String(q.offset))
      return get<{ data: Suppression[]; total: number; limit: number; offset: number }>(
        `/suppressions?${p}`)
    },
    add: (b: { address: string; reason?: string }) =>
      post<{ address: string; reason: string; already_suppressed?: boolean }>('/suppressions', b),
    remove: (address: string) => del<void>(`/suppressions/${encodeURIComponent(address)}`),
  },

  webhooks: {
    list: () => get<{ data: Webhook[]; available_events: string[] }>('/webhooks'),
    create: (b: { url: string; description?: string; events?: string[] }) =>
      post<CreatedWebhook>('/webhooks', b),
    update: (id: string, b: { enabled?: boolean; events?: string[]; url?: string }) =>
      patch<{ id: string; url: string; enabled: boolean }>(`/webhooks/${id}`, b),
    remove: (id: string) => del<void>(`/webhooks/${id}`),
  },

  activity: {
    list: (limit = 50, offset = 0) =>
      get<{ data: ActivityRow[]; total: number; recent_sign_ins: SignIn[] }>(
        `/activity?limit=${limit}&offset=${offset}`),
    apiRequests: (p: { limit?: number; offset?: number; errorsOnly?: boolean } = {}) => {
      const q = new URLSearchParams()
      q.set('limit', String(p.limit ?? 50))
      q.set('offset', String(p.offset ?? 0))
      if (p.errorsOnly) q.set('errors_only', 'true')
      return get<{
        data: Array<{ occurred_at: string; method: string; path: string; status_code: number; error_code?: string | null; source_ip: string; duration_ms: number; request_id: string }>
        total: number; error_count: number
        auth_failures: Array<{ occurred_at: string; result: string; source_ip: string; detail?: string | null; identity?: string | null }>
      }>(`/activity/api-requests?${q}`)
    },
  },

  stats: {
    get: (days = 30) => get<Stats>(`/stats?days=${days}`),
  },

  // Marketing-site content management (blog + community forum + tags). SuperAdmin only.
  content: {
    blogList: () => get<{ data: any[] }>('/content/admin/blog'),
    blogCreate: (b: any) => post<any>('/content/admin/blog', b),
    blogUpdate: (id: string, b: any) => request<any>(`/content/admin/blog/${id}`, { method: 'PATCH', body: JSON.stringify(b) }),
    blogDelete: (id: string) => del<any>(`/content/admin/blog/${id}`),
    threadList: (pending = false) => get<{ data: any[] }>(`/content/admin/community${pending ? '?pending=true' : ''}`),
    threadUpdate: (id: string, b: any) => request<any>(`/content/admin/community/${id}`, { method: 'PATCH', body: JSON.stringify(b) }),
    threadDelete: (id: string) => del<any>(`/content/admin/community/${id}`),
    replyUpdate: (id: string, b: any) => request<any>(`/content/admin/replies/${id}`, { method: 'PATCH', body: JSON.stringify(b) }),
    replyDelete: (id: string) => del<any>(`/content/admin/replies/${id}`),
    tagList: () => get<{ data: any[] }>('/content/admin/tags'),
    tagCreate: (b: any) => post<any>('/content/admin/tags', b),
    tagUpdate: (id: string, b: any) => request<any>(`/content/admin/tags/${id}`, { method: 'PATCH', body: JSON.stringify(b) }),
    tagDelete: (id: string) => del<any>(`/content/admin/tags/${id}`),
    // public read (for the admin to preview a thread's replies)
    thread: (slug: string) => get<{ thread: any; replies: any[] }>(`/content/community/${slug}`),
  },

  // Captured free-tool usage (SPF/DKIM/DMARC/BIMI checkers + inbox tester) for marketing/lead-gen.
  toolLeads: {
    list: (o: { tool?: string; country?: string; q?: string; page?: number; pageSize?: number } = {}) => {
      const p = new URLSearchParams()
      if (o.tool) p.set('tool', o.tool)
      if (o.country) p.set('country', o.country)
      if (o.q) p.set('q', o.q)
      p.set('page', String(o.page ?? 1))
      p.set('pageSize', String(o.pageSize ?? 50))
      return get<{ total: number; page: number; page_size: number; items: any[] }>(`/tools/admin/leads?${p}`)
    },
    stats: () => get<{
      total: number; last7: number
      by_tool: { label: string; count: number }[]
      by_country: { label: string; count: number }[]
    }>('/tools/admin/leads/stats'),
    exportUrl: (o: { tool?: string; country?: string } = {}) => {
      const p = new URLSearchParams()
      if (o.tool) p.set('tool', o.tool)
      if (o.country) p.set('country', o.country)
      return `/v1/tools/admin/leads/export?${p}`
    },
  },

  // Authenticated deliverability tester — same as the public tool, but every received result is
  // saved to this tenant's history (see /v1/mailtest). Same-origin, cookie-authenticated.
  mailtest: {
    newAddress: () => post<{ token: string; address: string; expires_minutes: number }>('/mailtest/new', {}),
    check: (token: string) => get<any>(`/mailtest/${token}`),
    history: () => get<{ data: any[] }>('/mailtest/history'),
  },

  reports: {
    byDomain: (days = 30) => get<{
      range_days: number
      domains: Array<{
        domain: string; messages: number; recipients: number
        delivered: number; bounced: number; deferred: number; complained: number; suppressed: number
        opened: number; clicked: number; delivery_rate: number | null; bounce_rate: number | null
      }>
      totals: {
        domains: number; messages: number; recipients: number
        delivered: number; bounced: number; deferred: number; complained: number; suppressed: number
        opened: number; clicked: number
      }
    }>(`/reports/by-domain?days=${days}`),
  },

  health: {
    get: (days = 30) => get<HealthReport>(`/health-monitor?days=${days}`),
  },

  bounces: {
    list: (kind = '') => get<BounceReport>(`/bounces${kind ? `?kind=${kind}` : ''}`),
  },

  billing: {
    get: () => get<BillingInfo>('/billing'),
    requestUpgrade: (plan: string) => post<{ requested: boolean; message: string }>('/billing/request-upgrade', { plan }),
  },

  team: {
    list: () => get<{ data: TeamMember[]; your_role: Role; your_email: string }>('/team'),
    create: (b: { email: string; password: string; role: string }) =>
      post<{ id: string; email: string; role: string; message: string }>('/team', b),
    update: (id: string, b: { role?: string; enabled?: boolean }) =>
      patch<{ id: string; email: string; role: string }>(`/team/${id}`, b),
    remove: (id: string) => del<void>(`/team/${id}`),
  },

  send: {
    message: (b: {
      from: string; to: string[]; cc?: string[]; bcc?: string[]; subject?: string; text?: string; html?: string
      attachments?: { filename: string; content_type?: string; content: string }[]
    }) => post<{ id: string; recipients: number; suppressed?: number }>('/messages', b),
    /** One separate message per entry (max 500 per call). bulk=true marks marketing mail. */
    batch: (b: {
      from: string; bulk?: boolean
      messages: { to: string[]; subject?: string; text?: string; html?: string; tags?: string[] }[]
    }) => post<{ queued: number; failed: number; results: { index: number; id?: string; status: string; recipients: number; error?: { code: string; message: string } }[] }>('/messages/batch', b),
  },

  inbound: {
    list: (q: { domain?: string; bounces?: boolean; limit?: number; offset?: number } = {}) => {
      const p = new URLSearchParams()
      if (q.domain) p.set('domain', q.domain)
      if (q.bounces !== undefined) p.set('bounces', String(q.bounces))
      if (q.limit) p.set('limit', String(q.limit))
      if (q.offset) p.set('offset', String(q.offset))
      return get<{ data: any[]; total: number; inbound_domains: number }>(`/inbound?${p}`)
    },
    get: (id: string) => get<any>(`/inbound/${id}`),
  },

  admin: {
    overview: () => get<any>('/admin/overview'),
    tenants: (q: { search?: string; status?: string; limit?: number; offset?: number } = {}) => {
      const p = new URLSearchParams()
      if (q.search) p.set('search', q.search)
      if (q.status) p.set('status', q.status)
      if (q.limit) p.set('limit', String(q.limit))
      if (q.offset) p.set('offset', String(q.offset))
      return get<{ data: any[]; total: number }>(`/admin/tenants?${p}`)
    },
    tenant: (id: string) => get<any>(`/admin/tenants/${id}`),
    updateTenant: (id: string, b: {
      plan_name?: string; status?: string; free_allowance_remaining?: number
      ramp_override_daily_limit?: number; reason?: string
    }) => request<any>(`/admin/tenants/${id}`, { method: 'PATCH', body: JSON.stringify(b) }),
    domains: (search = '') => get<{ data: any[]; total: number }>(`/admin/domains?search=${encodeURIComponent(search)}`),
    messages: (q: { search?: string; status?: string } = {}) => {
      const p = new URLSearchParams()
      if (q.search) p.set('search', q.search)
      if (q.status) p.set('status', q.status)
      return get<{ data: any[]; total: number }>(`/admin/messages?${p}`)
    },
    plans: () => get<{ data: any[] }>('/admin/plans'),
    updatePlan: (id: number, b: {
      daily?: number; hourly?: number; max_domains?: number; max_recipients?: number
      inbound_retention_days?: number; dedicated_ip?: boolean; tracking_allowed?: boolean
      mailbox_hosting?: boolean; max_api_keys?: number; max_smtp?: number
    }) => request<any>(`/admin/plans/${id}`, { method: 'PATCH', body: JSON.stringify({
      daily: b.daily, hourly: b.hourly, max_domains: b.max_domains, max_recipients: b.max_recipients,
      inbound_retention_days: b.inbound_retention_days, dedicated_ip: b.dedicated_ip,
      tracking_allowed: b.tracking_allowed, mailbox_hosting: b.mailbox_hosting,
      max_api_keys: b.max_api_keys, max_smtp: b.max_smtp,
    }) }),
    settings: () => get<{ require_owner_approval: boolean; auto_suspend_enabled: boolean; complaint_rate_threshold: number; hard_bounce_rate_threshold: number; anti_spam_min_volume: number; ses_relay_enabled: boolean; updated_at?: string; updated_by?: string }>('/admin/settings'),
    updateSettings: (b: { require_owner_approval?: boolean; auto_suspend_enabled?: boolean; complaint_rate_threshold?: number; hard_bounce_rate_threshold?: number; anti_spam_min_volume?: number; ses_relay_enabled?: boolean }) =>
      request<any>('/admin/settings', { method: 'PATCH', body: JSON.stringify(b) }),
    approveTenant: (id: string) =>
      request<any>(`/admin/tenants/${id}`, { method: 'PATCH', body: JSON.stringify({ status: 'sandbox' }) }),

    backupStatus: () => get<{ last_backup_at?: string; size_mb?: number; file?: string; stale: boolean }>('/admin/backup/status'),
    runBackup: () => post<{ ok: boolean; file: string; size_mb: number; duration_ms: number }>('/admin/backup/run', {}),

    bannedIps: () => get<{ bridge: boolean; updated_at?: number | null; banned: string[] }>('/admin/banned-ips'),
    unbanIp: (ip: string) => post<{ ok: boolean; ip: string; note: string }>('/admin/banned-ips/unban', { ip }),

    ipPools: () => get<{ data: any[] }>('/admin/ip-pools'),
    createIpPool: (b: { name: string; kind?: string; description?: string; isDefault?: boolean }) =>
      post<any>('/admin/ip-pools', { name: b.name, kind: b.kind, description: b.description, is_default: b.isDefault }),
    sendingIps: () => get<{ data: any[] }>('/admin/sending-ips'),
    createSendingIp: (b: { address: string; ipIndex: number; poolId: string; nodeId: string; ptrHostname?: string; heloName?: string; status?: string; dailyCap?: number }) =>
      post<any>('/admin/sending-ips', { address: b.address, ip_index: b.ipIndex, pool_id: b.poolId, node_id: b.nodeId, ptr_hostname: b.ptrHostname, helo_name: b.heloName, status: b.status, daily_cap: b.dailyCap }),
    updateSendingIp: (address: string, b: { status?: string; warmupStage?: number; dailyCap?: number; quarantineReason?: string }) =>
      request<any>(`/admin/sending-ips/${encodeURIComponent(address)}`, { method: 'PATCH', body: JSON.stringify({ status: b.status, warmup_stage: b.warmupStage, daily_cap: b.dailyCap, quarantine_reason: b.quarantineReason }) }),
    retireSendingIp: (address: string, release = false) =>
      del<any>(`/admin/sending-ips/${encodeURIComponent(address)}${release ? '?release=true' : ''}`),
    setRdns: (address: string) =>
      post<{ address: string; hostname: string; status: string }>(`/admin/sending-ips/${encodeURIComponent(address)}/rdns`, {}),
    mtaNodes: () => get<{ data: any[] }>('/admin/mta-nodes'),
    createMtaNode: (b: { hostname: string; privateIp: string; role?: string; provider?: string; region?: string }) =>
      post<any>('/admin/mta-nodes', { hostname: b.hostname, private_ip: b.privateIp, role: b.role, provider: b.provider, region: b.region }),
    assignPool: (b: { tenantId: string; poolId: string; priority?: number; reason?: string }) =>
      post<any>('/admin/tenant-ip-pools', { tenant_id: b.tenantId, pool_id: b.poolId, priority: b.priority, reason: b.reason }),
    unassignPool: (tenantId: string, poolId: string) =>
      del<any>(`/admin/tenant-ip-pools?tenant=${encodeURIComponent(tenantId)}&pool=${encodeURIComponent(poolId)}`),
    spfRecord: () => get<{ host: string; value: string; ip_count: number; note: string }>('/admin/spf-record'),
    audit: (q: { tenant?: string; action?: string; limit?: number; offset?: number } = {}) => {
      const p = new URLSearchParams()
      if (q.tenant) p.set('tenant', q.tenant)
      if (q.action) p.set('action', q.action)
      if (q.limit != null) p.set('limit', String(q.limit))
      if (q.offset != null) p.set('offset', String(q.offset))
      return get<{ data: any[]; total: number; limit: number; offset: number }>(`/admin/audit?${p}`)
    },
  },
}

// ---------------------------------------------------------------- display helpers

export const statusLabel: Record<DomainStatus, { text: string; cls: string; glyph: string }> = {
  pending:          { text: 'Not verified',    cls: 'pending',  glyph: '○' },
  partial:          { text: 'Partly verified', cls: 'pending',  glyph: '◐' },
  verified_sending: { text: 'Verified',        cls: 'ok',       glyph: '✓' },
  verified_inbound: { text: 'Verified + MX',   cls: 'ok',       glyph: '✓' },
  drifted:          { text: 'Records missing', cls: 'failed',   glyph: '!' },
  suspended:        { text: 'Suspended',       cls: 'critical', glyph: '⊘' },
}

/**
 * The registrar-panel mistakes we actually see, phrased as the fix rather than the
 * symptom. Generic "not found" text is what makes customers give up at the DNS step —
 * and the first two here are exactly what we hit ourselves while setting this up.
 */
export const hintText: Record<string, string> = {
  no_txt_record_found:
    'No TXT record at this name. If your registrar appends the domain automatically, enter only the part before your domain.',
  no_cname_record_found:
    'No CNAME at this name yet. DNS can take a few minutes; if it has been longer, check the host for a typo.',
  host_duplicated_by_registrar:
    'Your registrar appended the domain twice. Enter only the part before your domain in the host field.',
  cname_points_elsewhere:
    'A CNAME exists but points somewhere else. Replace its value with the one shown here.',
  token_does_not_match:
    'A TXT record exists but the token differs. Copy the value again — it is unique to this domain.',
  no_spf_record_found:
    'No SPF record found. Add the value shown, or merge the include into your existing SPF record.',
  multiple_spf_records:
    'You have more than one SPF record. That makes SPF fail outright — merge them into a single record.',
  spf_include_missing:
    'Your SPF record exists but does not include us. Add the include to the existing record; do not create a second one.',
  no_dmarc_record_found:
    'No DMARC record. Optional, but publishing one is strongly recommended.',
  no_mx_record_found:
    'No MX record found. Add both MX records to receive mail on this domain.',
  mx_points_elsewhere:
    'Your MX points to another provider. Replace it to receive mail here.',
  other_mx_hosts_present:
    'Another provider’s MX is also present. Mail will sometimes go there instead, which looks like random message loss.',
  dns_query_failed:
    'We could not reach your nameservers. That is a transient issue on our side — we retry automatically.',
}

export const purposeLabel: Record<string, string> = {
  ownership: 'Proves you own the domain',
  return_path: 'Bounce handling and DMARC alignment',
  dkim: 'Signs your mail so receivers can verify it',
  spf: 'Authorises our servers to send as you',
  dmarc: 'Tells receivers what to do with failures',
  tracking: 'Click and open tracking on your own domain',
  mx: 'Receives mail for this domain',
}

const reasonMap: Record<string, { text: string; cls: string }> = {
  hard_bounce: { text: 'Hard bounce', cls: 'critical' },
  hardbounce: { text: 'Hard bounce', cls: 'critical' },
  complaint: { text: 'Complaint', cls: 'critical' },
  unsubscribe: { text: 'Unsubscribed', cls: 'pending' },
  manual: { text: 'Added by you', cls: 'info' },
  soft_bounce_repeated: { text: 'Repeated soft bounce', cls: 'pending' },
  softbouncerepeated: { text: 'Repeated soft bounce', cls: 'pending' },
}

const msgStatusMap: Record<string, { text: string; cls: string }> = {
  accepted: { text: 'Accepted', cls: 'info' },
  queued: { text: 'Queued', cls: 'info' },
  sent: { text: 'Sent', cls: 'ok' },
  failed: { text: 'Failed', cls: 'critical' },
  blocked: { text: 'Blocked', cls: 'critical' },
}

const eventMap: Record<string, { text: string; cls: string }> = {
  accepted: { text: 'Accepted', cls: 'info' },
  queued: { text: 'Queued', cls: 'info' },
  delivered: { text: 'Delivered', cls: 'ok' },
  deferred: { text: 'Deferred', cls: 'pending' },
  bounced: { text: 'Bounced', cls: 'critical' },
  complained: { text: 'Complained', cls: 'critical' },
  opened: { text: 'Opened', cls: 'ok' },
  clicked: { text: 'Clicked', cls: 'ok' },
  unsubscribed: { text: 'Unsubscribed', cls: 'pending' },
  suppressed: { text: 'Suppressed', cls: 'pending' },
  blocked: { text: 'Blocked', cls: 'critical' },
}

/** Humanises the dotted audit action names into something a customer can read. */
export function actionLabel(action: string): string {
  const map: Record<string, string> = {
    'domain.created': 'Domain added',
    'domain.verified': 'Domain verified',
    'domain.deleted': 'Domain removed',
    'domain.dkim_rotated': 'DKIM key rotated',
    'api_key.created': 'API key created',
    'api_key.revoked': 'API key revoked',
    'smtp_credential.created': 'SMTP credential created',
    'smtp_credential.deleted': 'SMTP credential deleted',
    'suppression.added': 'Address suppressed',
    'suppression.removed': 'Suppression removed',
    'webhook.created': 'Webhook added',
    'webhook.updated': 'Webhook updated',
    'webhook.deleted': 'Webhook removed',
    'user.signed_up': 'Account created',
    'user.login': 'Signed in',
  }
  return map[action] ?? action.replace(/[._]/g, ' ').replace(/^./, c => c.toUpperCase())
}

/** "3 minutes ago" reads faster than a timestamp when scanning a log. */
export function relTime(iso?: string | null): string {
  if (!iso) return 'never'
  const then = new Date(iso).getTime()
  const secs = Math.round((Date.now() - then) / 1000)
  if (secs < 60) return 'just now'
  const mins = Math.round(secs / 60)
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.round(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  const days = Math.round(hrs / 24)
  if (days < 30) return `${days}d ago`
  return new Date(iso).toLocaleDateString()
}

/**
 * Enum values reach us as the server's own casing (`HardBounce`), while the label maps
 * below are written in snake_case for readability. Normalising both sides means a casing
 * change on the server degrades to a readable fallback instead of an undefined lookup.
 */
function normKey(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]/g, '')
}

function lookup<T>(map: Record<string, T>, key: string | undefined | null): T | undefined {
  if (!key) return undefined
  return map[key] ?? map[normKey(key)]
}

/** Title-cases an unknown enum value so it never renders as raw PascalCase. */
export function humanise(s: string): string {
  return s
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[._-]+/g, ' ')
    .replace(/^./, c => c.toUpperCase())
}

export function reasonBadge(reason: string) {
  return lookup(reasonMap, reason) ?? { text: humanise(reason), cls: 'info' }
}

export function statusBadge(status: string) {
  return lookup(msgStatusMap, status) ?? { text: humanise(status), cls: 'info' }
}

export function eventBadge(event: string) {
  return lookup(eventMap, event) ?? { text: humanise(event), cls: 'info' }
}

export const roleLabel: Record<string, { text: string; cls: string; can: string }> = {
  SuperAdmin: { text: 'Super admin', cls: 'critical', can: 'Full platform control' },
  Owner: { text: 'Owner', cls: 'info', can: 'Everything, including the team and billing' },
  Admin: { text: 'Admin', cls: 'info', can: 'Domains, keys, team — but not owners' },
  Developer: { text: 'Developer', cls: 'ok', can: 'Create keys and send' },
  Viewer: { text: 'Viewer', cls: 'pending', can: 'Read-only' },
}

// ---------------------------------------------------------------- hosted email accounts

export interface EmailAccount {
  id: number; address: string; enabled: boolean; quota_mb: number; used_mb: number
  last_login_at?: string | null; created_at: string
}

export interface EmailAccountsInfo {
  data: EmailAccount[]
  domains: { name: string; verified: boolean; mailboxes: number; mx_ok: boolean; mx_current: string[] }[]
  limits: { max: number; used: number; default_quota_mb: number }
  settings: {
    imap: { host: string; port: number; security: string }
    smtp: { host: string; port: number; security: string; alt_port: number }
    webmail_url: string
    mx: { priority: number; host: string }[]
  }
}

export const emailAccountsApi = {
  list: () => get<EmailAccountsInfo>('/email-accounts'),
  create: (b: { local_part: string; domain: string; password: string; quota_mb?: number }) => post<EmailAccount>('/email-accounts', b),
  update: (id: number, b: { password?: string; enabled?: boolean }) => patch<EmailAccount>(`/email-accounts/${id}`, b),
  remove: (id: number) => del<void>(`/email-accounts/${id}`),
}
